import { ChangeEvent } from "react";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { FormControlLabel } from "@remo-co/ui-core/src/components/FormControlLabel";
import { Radio } from "@remo-co/ui-core/src/components/Radio";
import { RadioGroup } from "@remo-co/ui-core/src/components/RadioGroup";
import { FormTextInput } from "@remo-co/ui-core/src/components/FormTextInput";
import { useI18n } from "i18n";
import { EventEmailCustomizationMediaType } from "modules/emailCustomization/types";
import { validateVideoURL } from "helpers/videoHelper";
import { BestUploadSizes } from "modules/uploader/UploadCaption";
import { ImageUploaderField } from "../ImageUploaderField";
import { useStyles } from "./styles";

interface MediaSectionProps {
  mediaType: EventEmailCustomizationMediaType;
  mediaURL: string;
  mediaExtraImageURL: string;
  onMediaTypeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onMediaURLChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onMediaImageUpload: (url: string) => void;
  onMediaImageDelete: () => void;
  onMediaExtraImageUpload: (url: string) => void;
  onMediaExtraImageDelete: () => void;
  mediaURLError?: string;
  mediaExtraImageURLError?: string;
}

export const MediaSection = ({
  mediaType,
  mediaURL,
  mediaExtraImageURL,
  onMediaTypeChange,
  onMediaURLChange,
  onMediaImageUpload,
  onMediaImageDelete,
  onMediaExtraImageUpload,
  onMediaExtraImageDelete,
  mediaURLError,
  mediaExtraImageURLError,
}: MediaSectionProps) => {
  const { t } = useI18n(["eventForm"]);
  const classes = useStyles();

  return (
    <Box mb={2}>
      <Typography variant="subtitle1" mb={1}>
        {t("eventForm:customize.emails.media.label")}
      </Typography>
      <RadioGroup
        aria-label="media-type"
        name="mediaType"
        value={mediaType}
        onChange={onMediaTypeChange}
        row
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          value={EventEmailCustomizationMediaType.IMAGE}
          control={<Radio />}
          label={t("eventForm:form.label.image")}
        />
        <FormControlLabel
          value={EventEmailCustomizationMediaType.VIDEO}
          control={<Radio />}
          label={t("eventForm:form.label.video")}
        />
      </RadioGroup>

      {mediaType === EventEmailCustomizationMediaType.IMAGE ? (
        <ImageUploaderField
          name="mediaURL"
          uploadedImage={mediaURL}
          onImageUpload={onMediaImageUpload}
          onImageDelete={onMediaImageDelete}
          bestSize={BestUploadSizes.videoThumbnail}
          dataTestId="media-image-uploader"
          error={mediaURLError}
        />
      ) : (
        <Box>
          <Box mb={2}>
            <FormTextInput
              label={t("eventForm:customize.emails.media.video.url.label")}
              value={mediaURL}
              onChange={onMediaURLChange}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              classes={{
                root: classes.textAreaRoot,
              }}
              error={
                !!mediaURLError || (!!mediaURL && !validateVideoURL(mediaURL))
              }
              helperText={
                mediaURLError || (!!mediaURL && !validateVideoURL(mediaURL))
                  ? t("eventForm:url.invalid")
                  : t("eventForm:form.url.placeholder")
              }
            />
          </Box>

          <ImageUploaderField
            name="mediaExtraImageURL"
            label={t("eventForm:customize.emails.media.video.thumbnail.label")}
            uploadedImage={mediaExtraImageURL}
            onImageUpload={onMediaExtraImageUpload}
            onImageDelete={onMediaExtraImageDelete}
            bestSize={BestUploadSizes.videoThumbnail}
            dataTestId="video-thumbnail-uploader"
            error={mediaExtraImageURLError}
          />
        </Box>
      )}
    </Box>
  );
};
